import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { TrendingUp, AlertCircle } from "lucide-react";

interface NetWorthSummaryCardProps {
  netWorth: number;
  displayCurrency: string;
  isLoading: boolean;
  errors: Record<string, Error | null>;
}

export function NetWorthSummaryCard({
  netWorth,
  displayCurrency,
  isLoading,
  errors,
}: NetWorthSummaryCardProps) {
  const failedSources = Object.entries(errors)
    .filter(([, err]) => err !== null)
    .map(([source]) => source);

  const hasErrors = failedSources.length > 0;

  const formattedValue = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: displayCurrency,
  }).format(netWorth);

  const isPositive = netWorth >= 0;

  return (
    <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-primary/90 to-primary text-primary-foreground shadow-elevated">
      {/* Decorative circle */}
      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10" aria-hidden="true" />
      <div className="absolute -right-4 top-12 h-20 w-20 rounded-full bg-white/5" aria-hidden="true" />

      <CardHeader className="relative pb-2">
        <CardTitle className="flex items-center gap-2 text-primary-foreground/80 text-sm font-medium">
          <TrendingUp className="h-4 w-4" aria-hidden="true" />
          Net Worth
        </CardTitle>
      </CardHeader>
      <CardContent className="relative space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-24 bg-white/20" />
            <Skeleton className="h-10 w-48 bg-white/20" />
          </div>
        ) : (
          <p
            className="text-3xl md:text-4xl font-bold tabular-nums tracking-tight"
            aria-label={`Net worth: ${formattedValue}`}
          >
            {formattedValue}
            <span className="ml-2 text-sm font-normal text-primary-foreground/60">
              {isPositive ? '↑' : '↓'}
            </span>
          </p>
        )}

        {hasErrors && (
          <Alert variant="destructive" className="bg-destructive/20 border-destructive/30 text-primary-foreground">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Some data could not be loaded</AlertTitle>
            <AlertDescription>
              Net worth may be incomplete. Failed sources:{" "}
              {failedSources.join(", ")}.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
